import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronDown, ChevronUp, Upload, Plus, Trash2, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';

interface FreightItem {
  id: string;
  description: string;
  quantity: string;
  quantity_unit: string;
  weight: string;
  weight_unit: string;
  declared_value: string;
}

interface Charge {
  id: string;
  charge_type: string;
  charge_name: string;
  rate: string;
  amount: number;
}

interface Stop {
  id: string;
  stop_type: string;
  stop_order: number;
  facility_name: string;
  street: string;
  city: string;
  state: string;
  zip_code: string;
  date: string;
  time_from: string;
  time_to: string;
  contact_person: string;
  phone_number: string;
  phone_extension: string;
  instructions: string;
}

export function EditLoadPage({ loadId }: { loadId: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [actualLoadId, setActualLoadId] = useState<string>('');

  const toAmountNumber = (value: string) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };
  
  // Update URL when component loads
  useEffect(() => {
    if (loadId) {
      window.history.replaceState({}, '', `/loads/${loadId}`);
    }
  }, [loadId]);
  
  // Accordion states
  const [routeExpanded, setRouteExpanded] = useState(true);
  const [freightExpanded, setFreightExpanded] = useState(true);
  const [billingExpanded, setBillingExpanded] = useState(true);
  
  // Basic Info
  const [formData, setFormData] = useState({
    rate_confirmation_number: '',
    booked_by: '',
    bol_numbers: '',
    load_number: '',
    customer_id: '',
    carrier: '',
    commodity: '',
    weight: '',
    total_rate: '',
    total_miles: '',
  });

  // Shipper
  const [shipper, setShipper] = useState({
    id: '',
    facility_name: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    date: '',
    time_from: '',
    time_to: '',
    contact_person: '',
    phone_number: '',
    phone_extension: '',
    instructions: '',
  });

  // Intermediate Stops
  const [intermediateStops, setIntermediateStops] = useState<Stop[]>([]);

  // Consignee
  const [consignee, setConsignee] = useState({
    id: '',
    facility_name: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    date: '',
    time_from: '',
    time_to: '',
    contact_person: '',
    phone_number: '',
    phone_extension: '',
    instructions: '',
    rescheduled_date: '',
    rescheduled_time_from: '',
    rescheduled_time_to: '',
  });

  // Freight Items
  const [freightItems, setFreightItems] = useState<FreightItem[]>([]);

  // Billing
  const [billing, setBilling] = useState({
    customer_name: '',
    contact_info: '',
    street: '',
    city: '',
    state: '',
    zip_code: '',
    email: '',
    phone_number: '',
    phone_extension: '',
    factoring_company: '',
    factoring_fee_percentage: '',
    transfer_documents: true,
    send_to_customer: true,
    transfer_to_factoring: true,
  });

  // Charges
  const [charges, setCharges] = useState<Charge[]>([]);

  // Document upload
  const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);

  // Dispatch info
  const [dispatchInfo, setDispatchInfo] = useState<{
    id: string;
    driver_id: string | null;
    team_id: string | null;
    driver_name: string;
    truck_number: string;
    driver_pay: number | null;
    empty_miles: number | null;
  } | null>(null);
  // Driver reassignment (mirrors DispatchBoardView.handleReassignLoad)
  const [editingDriver, setEditingDriver] = useState(false);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [savingDriver, setSavingDriver] = useState(false);
  const [driverSearch, setDriverSearch] = useState('');
  const [driverOptions, setDriverOptions] = useState<{ id: string; name: string; teamLabel: string | null; truckNumber: string | null }[]>([]);
  const [driverOptionsLoaded, setDriverOptionsLoaded] = useState(false);
  const [teamsByDriverId, setTeamsByDriverId] = useState<Map<string, { teamId: string; primaryId: string; label: string }>>(new Map());
  const [editingDriverPay, setEditingDriverPay] = useState(false);
  const [driverPayValue, setDriverPayValue] = useState<string>('');
  const [editingEmptyMiles, setEditingEmptyMiles] = useState(false);
  const [emptyMilesValue, setEmptyMilesValue] = useState<string>('');
  const [carrierCompanies, setCarrierCompanies] = useState<{ id: string; name: string; code: string | null }[]>([]);

  // Load data from database
  useEffect(() => {
    loadLoadData();
  }, [loadId]);

  useEffect(() => {
    loadCarrierCompanies();
  }, []);

  // Carrier options come from this tenant's companies table - hardcoding them would
  // let the user save a carrier that matches no company row, which hides the load
  // from the Ready for Billing screen.
  const loadCarrierCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, code')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;

      setCarrierCompanies(data || []);
    } catch (error) {
      console.error('Error loading carrier companies:', error);
    }
  };

  const loadLoadData = async () => {
    try {
      setDataLoading(true);
      console.log('Loading load data for ID:', loadId);

      // 1. Load main load data - lookup by UUID (loadId is the UUID from the URL)
      const { data: loadData, error: loadError } = await supabase
        .from('loads')
        .select('*')
        .eq('id', loadId)
        .single();

      if (loadError) {
        console.error('Error loading load:', loadError);
        throw loadError;
      }

      console.log('Load data loaded:', loadData);

      // Store the actual UUID for future operations
      setActualLoadId(loadData.id);

      const newFormData = {
        rate_confirmation_number: loadData.rate_confirmation_number || '',
        booked_by: loadData.booked_by || '',
        bol_numbers: loadData.bol_numbers || '',
        load_number: loadData.load_number || '',
        customer_id: loadData.customer_id || '',
        carrier: loadData.carrier || '',
        commodity: loadData.commodity || '',
        weight: loadData.weight?.toString() || '',
        total_rate: loadData.total_rate?.toString() || '',
        total_miles: loadData.total_miles?.toString() || '',
      };
      
      console.log('Setting form data:', newFormData);
      setFormData(newFormData);
      
      // Verify state was set
      setTimeout(() => {
        console.log('Form data after setState:', formData);
      }, 100);

      // 2. Load stops
      const { data: stopsData, error: stopsError } = await supabase
        .from('load_stops')
        .select('*')
        .eq('load_id', loadData.id)
        .order('stop_order');

      if (stopsError) throw stopsError;

      console.log('Stops data loaded:', stopsData);

      if (stopsData && stopsData.length > 0) {
        const shipperStop = stopsData.find(s => s.stop_type === 'shipper');
        const consigneeStop = stopsData.find(s => s.stop_type === 'consignee');
        const intermediateStopsData = stopsData.filter(s => s.stop_type === 'stop');
        
        console.log('Shipper stop:', shipperStop);
        console.log('Consignee stop:', consigneeStop);

        if (shipperStop) {
          setShipper({
            id: shipperStop.id,
            facility_name: shipperStop.facility_name || '',
            street: shipperStop.street || '',
            city: shipperStop.city || '',
            state: shipperStop.state || '',
            zip_code: shipperStop.zip_code || '',
            date: shipperStop.date || '',
            time_from: shipperStop.time_from || '',
            time_to: shipperStop.time_to || '',
            contact_person: shipperStop.contact_person || '',
            phone_number: shipperStop.phone_number || '',
            phone_extension: shipperStop.phone_extension || '',
            instructions: shipperStop.instructions || '',
          });
        }

        if (consigneeStop) {
          setConsignee({
            id: consigneeStop.id,
            facility_name: consigneeStop.facility_name || '',
            street: consigneeStop.street || '',
            city: consigneeStop.city || '',
            state: consigneeStop.state || '',
            zip_code: consigneeStop.zip_code || '',
            date: consigneeStop.date || '',
            time_from: consigneeStop.time_from || '',
            time_to: consigneeStop.time_to || '',
            contact_person: consigneeStop.contact_person || '',
            phone_number: consigneeStop.phone_number || '',
            phone_extension: consigneeStop.phone_extension || '',
            instructions: consigneeStop.instructions || '',
            rescheduled_date: loadData.rescheduled_delivery_date || '',
            rescheduled_time_from: loadData.rescheduled_delivery_time_from || '',
            rescheduled_time_to: loadData.rescheduled_delivery_time_to || '',
          });
        }

        setIntermediateStops(intermediateStopsData.map(stop => ({
          id: stop.id,
          stop_type: stop.stop_type,
          stop_order: stop.stop_order,
          facility_name: stop.facility_name || '',
          street: stop.street || '',
          city: stop.city || '',
          state: stop.state || '',
          zip_code: stop.zip_code || '',
          date: stop.date || '',
          time_from: stop.time_from || '',
          time_to: stop.time_to || '',
          contact_person: stop.contact_person || '',
          phone_number: stop.phone_number || '',
          phone_extension: stop.phone_extension || '',
          instructions: stop.instructions || '',
        })));
      } else {
        // Fallback: If no load_stops exist (AI-created loads), populate from loads table fields
        console.log('No load_stops found, using loads table fields as fallback');
        setShipper({
          id: '',
          facility_name: '',
          street: '',
          city: loadData.pickup_city || '',
          state: loadData.pickup_state || '',
          zip_code: '',
          date: loadData.pickup_date || '',
          time_from: '',
          time_to: '',
          contact_person: '',
          phone_number: '',
          phone_extension: '',
          instructions: '',
        });
        
        setConsignee({
          id: '',
          facility_name: '',
          street: '',
          city: loadData.delivery_city || '',
          state: loadData.delivery_state || '',
          zip_code: '',
          date: loadData.delivery_date || '',
          time_from: '',
          time_to: '',
          contact_person: '',
          phone_number: '',
          phone_extension: '',
          instructions: '',
          rescheduled_date: loadData.rescheduled_delivery_date || '',
          rescheduled_time_from: loadData.rescheduled_delivery_time_from || '',
          rescheduled_time_to: loadData.rescheduled_delivery_time_to || '',
        });
      }

      // 3. Load freight items - DISABLED: freight_items table doesn't exist
      // TODO: Implement freight items using correct table if needed
      // const { data: freightData, error: freightError } = await supabase
      //   .from('freight_items')
      //   .select('*')
      //   .eq('load_id', loadData.id);
      //
      // if (freightError) throw freightError;
      //
      // if (freightData && freightData.length > 0) {
      //   setFreightItems(freightData.map(item => ({
      //     id: item.id,
      //     description: item.description || '',
      //     quantity: item.quantity?.toString() || '',
      //     quantity_unit: item.quantity_unit || 'Pallet(s)',
      //     weight: item.weight?.toString() || '',
      //     weight_unit: item.weight_unit || 'lbs',
      //     declared_value: item.declared_value?.toString() || '',
      //   })));
      // }

      // 4. Load charges
      const { data: chargesData, error: chargesError } = await supabase
        .from('load_charges')
        .select('*')
        .eq('load_id', loadData.id);

      if (chargesError) throw chargesError;

      if (chargesData && chargesData.length > 0) {
        setCharges(chargesData.map(charge => ({
          id: charge.id,
          charge_type: charge.charge_type,
          charge_name: charge.charge_name,
          rate: charge.rate?.toString() || '',
          amount: charge.amount || 0,
        })));
      }

      // 5. Load dispatch info
      const { data: dispatchData } = await supabase
        .from('dispatches')
        .select(`
          id,
          driver_id,
          team_id,
          driver_pay,
          empty_miles,
          drivers (first_name, last_name),
          trucks (unit_number)
        `)
        .eq('load_id', loadData.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (dispatchData) {
        const driver = Array.isArray(dispatchData.drivers) ? dispatchData.drivers[0] : dispatchData.drivers;
        const truck = Array.isArray(dispatchData.trucks) ? dispatchData.trucks[0] : dispatchData.trucks;

        setDispatchInfo({
          id: dispatchData.id,
          driver_id: (dispatchData as any).driver_id ?? null,
          team_id: (dispatchData as any).team_id ?? null,
          driver_name: driver ? `${driver.first_name} ${driver.last_name}` : 'N/A',
          truck_number: truck?.unit_number || 'N/A',
          driver_pay: dispatchData.driver_pay,
          empty_miles: (dispatchData as any).empty_miles ?? null,
        });
        setDriverPayValue(dispatchData.driver_pay?.toString() || '');
        setEmptyMilesValue(((dispatchData as any).empty_miles ?? 0).toString());
      }

      // 6. Load billing info
      const { data: billingData, error: billingError } = await supabase
        .from('load_billing_info')
        .select('*')
        .eq('load_id', loadData.id)
        .single();

      if (!billingError && billingData) {
        setBilling({
          customer_name: billingData.customer_name || '',
          contact_info: billingData.contact_info || '',
          street: billingData.street || '',
          city: billingData.city || '',
          state: billingData.state || '',
          zip_code: billingData.zip_code || '',
          email: billingData.email || '',
          phone_number: billingData.phone_number || '',
          phone_extension: billingData.phone_extension || '',
          factoring_company: loadData.factoring_company || '',
          factoring_fee_percentage: loadData.factoring_fee_percentage?.toString() || '',
          transfer_documents: loadData.transfer_documents ?? true,
          send_to_customer: loadData.send_to_customer ?? true,
          transfer_to_factoring: loadData.transfer_to_factoring ?? true,
        });
      }

    } catch (error) {
      console.error('Error loading load data:', error);
      toast.error('Failed to load load data');
    } finally {
      // Small delay to ensure all state updates are processed before hiding loading screen
      setTimeout(() => {
        setDataLoading(false);
      }, 100);
    }
  };

  const addFreightItem = () => {
    setFreightItems([
      ...freightItems,
      {
        id: Date.now().toString(),
        description: '',
        quantity: '',
        quantity_unit: 'Pallet(s)',
        weight: '',
        weight_unit: 'lbs',
        declared_value: '',
      },
    ]);
  };

  const removeFreightItem = (id: string) => {
    setFreightItems(freightItems.filter((item) => item.id !== id));
  };

  const updateFreightItem = (id: string, field: string, value: string) => {
    setFreightItems(
      freightItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const addAccessorialFee = () => {
    setCharges([
      ...charges,
      {
        id: Date.now().toString(),
        charge_type: 'accessorial',
        charge_name: '',
        rate: '',
        amount: 0,
      },
    ]);
  };

  const removeCharge = (id: string) => {
    setCharges(charges.filter((charge) => charge.id !== id));
  };

  const updateCharge = (id: string, field: string, value: string) => {
    setCharges(
      charges.map((charge) =>
        charge.id === id ? { ...charge, [field]: value } : charge
      )
    );
  };

  const calculateTotals = () => {
    const haulingFee = charges.find(c => c.charge_type === 'hauling');
    const payeeHaulingFee = charges.find(c => c.charge_type === 'payee_hauling');
    const fuelSurcharge = charges.find(c => c.charge_type === 'fuel_surcharge');
    const accessorialFees = charges.filter(c => c.charge_type === 'accessorial');

    const haulingAmount = parseFloat(haulingFee?.rate || '0');
    const payeeHaulingAmount = parseFloat(payeeHaulingFee?.rate || '0');
    const fuelAmount = parseFloat(fuelSurcharge?.rate || '0');
    const accessorialTotal = accessorialFees.reduce((sum, fee) => sum + parseFloat(fee.rate || '0'), 0);

    const grossAmount = haulingAmount + fuelAmount + accessorialTotal;
    const payeeGrossAmount = payeeHaulingAmount + fuelAmount + accessorialTotal;

    return {
      grossAmount,
      payeeGrossAmount,
    };
  };

  const handleDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setUploadedDocuments(prev => [...prev, ...newFiles]);
    }
  };

  // Load the driver list + active teams once, when the user opens the driver editor.
  const loadDriverOptions = async () => {
    if (driverOptionsLoaded) return;
    try {
      const [{ data: driverRows }, { data: teamRows }] = await Promise.all([
        supabase
          .from('drivers')
          .select('id, first_name, last_name, status, is_active, equipment_assignments(is_current, trucks(unit_number))')
          .order('first_name', { ascending: true }),
        supabase
          .from('driver_teams')
          .select('id, name, driver_team_members(driver_id, created_at, drivers(first_name, last_name))')
          .eq('is_active', true),
      ]);

      // Team map: first member (by created_at) is the primary the team's loads normalize onto.
      const teamMap = new Map<string, { teamId: string; primaryId: string; label: string }>();
      (teamRows || []).forEach((t: any) => {
        const members = [...(t.driver_team_members || [])].sort(
          (a: any, b: any) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
        if (members.length === 0) return;
        const label = members
          .map((m: any) => (m.drivers ? `${m.drivers.first_name} ${m.drivers.last_name}` : ''))
          .filter(Boolean)
          .join(' / ');
        const primaryId = members[0].driver_id;
        members.forEach((m: any) => teamMap.set(m.driver_id, { teamId: t.id, primaryId, label }));
      });
      setTeamsByDriverId(teamMap);

      const currentId = dispatchInfo?.driver_id;
      const options = (driverRows || [])
        .filter((d: any) => {
          if (d.id === currentId) return true; // always keep the current driver selectable
          if (d.is_active === false) return false;
          const st = String(d.status || '').toLowerCase();
          return st !== 'terminated' && st !== 'inactive';
        })
        .map((d: any) => {
          const team = teamMap.get(d.id);
          const assignments = (d.equipment_assignments as any[]) || [];
          const current = assignments.find((a: any) => a.is_current === true) || assignments[0];
          return {
            id: d.id,
            name: `${d.first_name} ${d.last_name}`,
            teamLabel: team ? team.label : null,
            truckNumber: current?.trucks?.unit_number || null,
          };
        })
        .sort((a: any, b: any) => a.name.localeCompare(b.name));
      setDriverOptions(options);
      setDriverOptionsLoaded(true);
    } catch (error) {
      console.error('Error loading drivers:', error);
      toast.error('Failed to load drivers');
    }
  };

  const handleStartEditDriver = () => {
    if (!dispatchInfo) return;
    setSelectedDriverId(dispatchInfo.driver_id || '');
    setDriverSearch('');
    setEditingDriver(true);
    loadDriverOptions();
  };

  const handleSaveDriver = async () => {
    if (!dispatchInfo || !selectedDriverId) return;
    if (selectedDriverId === dispatchInfo.driver_id) {
      setEditingDriver(false);
      return;
    }

    setSavingDriver(true);
    try {
      // Find the new driver's current truck so the dispatch + load move to the right equipment.
      const { data: driverData } = await supabase
        .from('drivers')
        .select('first_name, last_name, equipment_assignments(is_current, trucks(id, unit_number))')
        .eq('id', selectedDriverId)
        .single();

      const assignments = ((driverData as any)?.equipment_assignments as any[]) || [];
      const currentAssignment = assignments.find((a: any) => a.is_current === true) || assignments[0];
      const truckId: string | undefined = currentAssignment?.trucks?.id;
      const truckNumber: string | undefined = currentAssignment?.trucks?.unit_number;

      // Team driver → normalize onto the team's primary member and tag team_id.
      // Solo driver → clear team_id.
      const team = teamsByDriverId.get(selectedDriverId);
      const dispatchDriverId = team ? team.primaryId : selectedDriverId;
      const teamId = team ? team.teamId : null;
      const now = new Date().toISOString();

      // 1) Dispatch row (drives the board + dispatcher gross view).
      const dispatchPayload: any = { driver_id: dispatchDriverId, team_id: teamId, updated_at: now };
      if (truckId) dispatchPayload.truck_id = truckId;
      const { error: dispatchError } = await supabase
        .from('dispatches')
        .update(dispatchPayload)
        .eq('id', dispatchInfo.id);
      if (dispatchError) throw dispatchError;

      // 2) Load row — accounting, statements and reporting read loads.driver_id directly.
      const loadPayload: any = { driver_id: dispatchDriverId, team_id: teamId, updated_at: now };
      if (truckId) loadPayload.truck_id = truckId;
      const { error: loadError } = await supabase
        .from('loads')
        .update(loadPayload)
        .eq('id', loadId);
      if (loadError) throw loadError;

      const newName = team
        ? team.label
        : driverData
          ? `${(driverData as any).first_name} ${(driverData as any).last_name}`
          : 'N/A';

      setDispatchInfo({
        ...dispatchInfo,
        driver_id: dispatchDriverId,
        team_id: teamId,
        driver_name: newName,
        truck_number: truckNumber || dispatchInfo.truck_number,
      });
      setEditingDriver(false);
      toast.success(`Load reassigned to ${newName}. Review driver pay if needed.`);
    } catch (error) {
      console.error('Error reassigning driver:', error);
      toast.error('Failed to reassign driver');
    } finally {
      setSavingDriver(false);
    }
  };

  const handleSaveDriverPay = async () => {
    if (!dispatchInfo) return;

    try {
      const payAmount = toAmountNumber(driverPayValue);

      const { error } = await supabase
        .from('dispatches')
        .update({ driver_pay: payAmount })
        .eq('id', dispatchInfo.id);

      if (error) throw error;

      setDispatchInfo({ ...dispatchInfo, driver_pay: payAmount });
      setEditingDriverPay(false);
      toast.success('Driver pay updated successfully');
    } catch (error) {
      console.error('Error updating driver pay:', error);
      toast.error('Failed to update driver pay');
    }
  };

  const handleSaveEmptyMiles = async () => {
    if (!dispatchInfo) return;

    try {
      const miles = parseInt(emptyMilesValue) || 0;

      const { error } = await supabase
        .from('dispatches')
        .update({ empty_miles: miles })
        .eq('id', dispatchInfo.id);

      if (error) throw error;

      setDispatchInfo({ ...dispatchInfo, empty_miles: miles });
      setEditingEmptyMiles(false);
      toast.success('Empty miles updated successfully');
    } catch (error) {
      console.error('Error updating empty miles:', error);
      toast.error('Failed to update empty miles');
    }
  };

  function removeDocument(index: number) {
    setUploadedDocuments(prev => prev.filter((_, i) => i !== index));
  }

  async function uploadDocumentsToLoad(loadNumber: string) {
    if (uploadedDocuments.length === 0) return;

    setUploading(true);
    try {
      // Get username of the user who created/booked the load
      let userName = 'Unknown User';
      if (formData.booked_by) {
        const { data: userData } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('id', formData.booked_by)
          .single();
        
        if (userData) {
          // Use full name if available, otherwise use email username
          userName = userData.full_name || userData.email?.split('@')[0] || 'Unknown User';
        }
      }
      
      // First, ensure LOADS parent folder exists
      const { data: existingLoadsFolder, error: loadsFolderQueryError } = await supabase
        .from('document_folders')
        .select('id')
        .eq('name', 'LOADS')
        .is('parent_folder_id', null)
        .maybeSingle();

      if (loadsFolderQueryError) throw loadsFolderQueryError;

      let loadsFolderId: string;
      
      if (!existingLoadsFolder) {
        const { data: newLoadsFolder, error: loadsFolderError } = await supabase
          .from('document_folders')
          .insert([{ name: 'LOADS', parent_folder_id: null }])
          .select()
          .single();
        
        if (loadsFolderError || !newLoadsFolder) throw loadsFolderError;
        loadsFolderId = newLoadsFolder.id;
      } else {
        loadsFolderId = existingLoadsFolder.id;
      }

      // Create or get user folder under LOADS
      const { data: existingUserFolder, error: userFolderQueryError } = await supabase
        .from('document_folders')
        .select('id')
        .eq('name', userName)
        .eq('parent_folder_id', loadsFolderId)
        .maybeSingle();

      if (userFolderQueryError) throw userFolderQueryError;

      let userFolderId: string;

      if (!existingUserFolder) {
        const { data: newUserFolder, error: userFolderError } = await supabase
          .from('document_folders')
          .insert([{ name: userName, parent_folder_id: loadsFolderId }])
          .select()
          .single();
        
        if (userFolderError || !newUserFolder) throw userFolderError;
        userFolderId = newUserFolder.id;
      } else {
        userFolderId = existingUserFolder.id;
      }

      // Create or get load-specific folder under user folder
      const { data: existingLoadFolder, error: loadFolderQueryError } = await supabase
        .from('document_folders')
        .select('id')
        .eq('name', loadNumber)
        .eq('parent_folder_id', userFolderId)
        .maybeSingle();

      if (loadFolderQueryError) throw loadFolderQueryError;

      let loadFolderId: string;

      if (!existingLoadFolder) {
        const { data: newLoadFolder, error: loadFolderError } = await supabase
          .from('document_folders')
          .insert([{ 
            name: loadNumber, 
            parent_folder_id: userFolderId 
          }])
          .select()
          .single();
        
        if (loadFolderError || !newLoadFolder) throw loadFolderError;
        loadFolderId = newLoadFolder.id;
      } else {
        loadFolderId = existingLoadFolder.id;
      }

      // Upload each document
      for (const file of uploadedDocuments) {
        // Sanitize filename: replace spaces and special characters
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
        const fileName = `${Date.now()}_${sanitizedName}`;
        const sanitizedUserName = userName.replace(/[^a-zA-Z0-9.-]/g, '_');
        const filePath = `LOADS/${sanitizedUserName}/${loadNumber}/${fileName}`;
        
        // Upload file to Supabase Storage (use 'company-documents' bucket)
        const { error: uploadError } = await supabase.storage
          .from('company-documents')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) {
          console.error('Storage upload error:', uploadError);
          throw new Error(`Failed to upload ${file.name}: ${uploadError.message}`);
        }

        // Create document record in database
        const { error: dbError } = await supabase
          .from('documents')
          .insert([{
            name: file.name,
            file_path: filePath,
            file_size: file.size,
            file_type: file.type || 'application/octet-stream',
            mime_type: file.type,
            folder_id: loadFolderId,
          }]);

        if (dbError) throw dbError;
      }

      toast.success(`${uploadedDocuments.length} document(s) uploaded successfully!`);
      setUploadedDocuments([]); // Clear the uploaded documents
    } catch (error: any) {
      console.error('Error uploading documents:', error);
      toast.error(error.message || 'Failed to upload documents');
    } finally {
      setUploading(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Update load using the actual UUID
      const { error: loadError } = await supabase
        .from('loads')
        .update({
          load_number: formData.load_number,
          customer_name: billing.customer_name || null,
          carrier: formData.carrier,
          commodity: formData.commodity,
          weight: parseInt(formData.weight),
          total_rate: toAmountNumber(formData.total_rate),
          total_miles: parseInt(formData.total_miles),
          pickup_date: shipper.date,
          delivery_date: consignee.date,
          rescheduled_delivery_date: consignee.rescheduled_date || null,
          rescheduled_delivery_time_from: consignee.rescheduled_time_from || null,
          rescheduled_delivery_time_to: consignee.rescheduled_time_to || null,
          rate_confirmation_number: formData.rate_confirmation_number,
          booked_by: formData.booked_by,
          bol_numbers: formData.bol_numbers,
          factoring_fee_percentage: parseFloat(billing.factoring_fee_percentage) || null,
          transfer_documents: billing.transfer_documents,
          send_to_customer: billing.send_to_customer,
          transfer_to_factoring: billing.transfer_to_factoring,
        })
        .eq('id', actualLoadId);

      if (loadError) throw loadError;

      // Update or create shipper stop
      if (shipper.id) {
        // Update existing shipper stop
        const { error: shipperError } = await supabase
          .from('load_stops')
          .update({
            facility_name: shipper.facility_name,
            street: shipper.street,
            city: shipper.city,
            state: shipper.state,
            zip_code: shipper.zip_code,
            date: shipper.date,
            time_from: shipper.time_from || null,
            time_to: shipper.time_to || null,
            contact_person: shipper.contact_person,
            phone_number: shipper.phone_number,
            phone_extension: shipper.phone_extension,
            instructions: shipper.instructions,
          })
          .eq('id', shipper.id);

        if (shipperError) throw shipperError;
      } else {
        // Create new shipper stop (for AI-created loads that don't have stops)
        const { error: shipperError } = await supabase
          .from('load_stops')
          .insert({
            load_id: actualLoadId,
            stop_type: 'shipper',
            stop_order: 1,
            facility_name: shipper.facility_name || '',
            street: shipper.street || '',
            city: shipper.city || '',
            state: shipper.state || '',
            zip_code: shipper.zip_code || '',
            date: shipper.date || new Date().toISOString().split('T')[0],
            time_from: shipper.time_from || '00:00:00',
            time_to: shipper.time_to || null,
            contact_person: shipper.contact_person || '',
            phone_number: shipper.phone_number || '',
            phone_extension: shipper.phone_extension || '',
            instructions: shipper.instructions || '',
          });

        if (shipperError) throw shipperError;
      }

      // Update or create consignee stop
      if (consignee.id) {
        // Update existing consignee stop
        const { error: consigneeError } = await supabase
          .from('load_stops')
          .update({
            facility_name: consignee.facility_name,
            street: consignee.street,
            city: consignee.city,
            state: consignee.state,
            zip_code: consignee.zip_code,
            date: consignee.date,
            time_from: consignee.time_from || null,
            time_to: consignee.time_to || null,
            contact_person: consignee.contact_person,
            phone_number: consignee.phone_number,
            phone_extension: consignee.phone_extension,
            instructions: consignee.instructions,
          })
          .eq('id', consignee.id);

        if (consigneeError) throw consigneeError;
      } else {
        // Create new consignee stop (for AI-created loads that don't have stops)
        const { error: consigneeError } = await supabase
          .from('load_stops')
          .insert({
            load_id: actualLoadId,
            stop_type: 'consignee',
            stop_order: 2,
            facility_name: consignee.facility_name || '',
            street: consignee.street || '',
            city: consignee.city || '',
            state: consignee.state || '',
            zip_code: consignee.zip_code || '',
            date: consignee.date || new Date().toISOString().split('T')[0],
            time_from: consignee.time_from || '00:00:00',
            time_to: consignee.time_to || null,
            contact_person: consignee.contact_person || '',
            phone_number: consignee.phone_number || '',
            phone_extension: consignee.phone_extension || '',
            instructions: consignee.instructions || '',
          });

        if (consigneeError) throw consigneeError;
      }

      // Also update the loads table pickup/delivery city/state for backward compatibility
      const { error: loadsUpdateError } = await supabase
        .from('loads')
        .update({
          pickup_city: shipper.city,
          pickup_state: shipper.state,
          delivery_city: consignee.city,
          delivery_state: consignee.state,
        })
        .eq('id', actualLoadId);

      if (loadsUpdateError) throw loadsUpdateError;

      // Persist billing details (update existing row or insert new row)
      const billingPayload = {
        load_id: actualLoadId,
        customer_name: billing.customer_name || null,
        contact_info: billing.contact_info || null,
        street: billing.street || null,
        city: billing.city || null,
        state: billing.state || null,
        zip_code: billing.zip_code || null,
        email: billing.email || null,
        phone_number: billing.phone_number || null,
        phone_extension: billing.phone_extension || null,
      };

      const { data: existingBilling, error: billingFindError } = await supabase
        .from('load_billing_info')
        .select('id')
        .eq('load_id', actualLoadId)
        .maybeSingle();

      if (billingFindError) throw billingFindError;

      if (existingBilling?.id) {
        const { error: billingUpdateError } = await supabase
          .from('load_billing_info')
          .update(billingPayload)
          .eq('id', existingBilling.id);

        if (billingUpdateError) throw billingUpdateError;
      } else {
        const { error: billingInsertError } = await supabase
          .from('load_billing_info')
          .insert(billingPayload);

        if (billingInsertError) throw billingInsertError;
      }

      // Keep Address Book customer in sync if this load is linked to a customer
      if (formData.customer_id) {
        const customerPayload = {
          name: billing.customer_name || null,
          billing_street: billing.street || null,
          billing_city: billing.city || null,
          billing_state: billing.state || null,
          billing_zip: billing.zip_code || null,
          billing_email: billing.email || null,
          billing_phone: billing.phone_number || null,
        };

        const { error: customerUpdateError } = await supabase
          .from('customers')
          .update(customerPayload)
          .eq('id', formData.customer_id);

        if (customerUpdateError) {
          // Some environments have partial customers schema; ensure at least name sync succeeds
          const { error: customerNameOnlyError } = await supabase
            .from('customers')
            .update({ name: billing.customer_name || null })
            .eq('id', formData.customer_id);

          if (customerNameOnlyError) throw customerNameOnlyError;
        }
      }

      // Upload documents if any were selected
      if (uploadedDocuments.length > 0) {
        await uploadDocumentsToLoad(formData.load_number);
      }

      toast.success('Load updated successfully!');
      navigate('/loads');
    } catch (error) {
      console.error('Error updating load:', error);
      toast.error('Failed to update load');
    } finally {
      setLoading(false);
    }
  };

  if (dataLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading load data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => navigate('/loads')}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Edit Load</h1>
                <p className="text-sm text-slate-600">{formData.load_number}</p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate('/loads')}
                className="px-6 py-2.5 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                onClick={handleSubmit}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <form key={actualLoadId || 'new'} onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info Section */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-6">Basic Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Rate Confirmation No.*
                </label>
                <input
                  type="text"
                  value={formData.rate_confirmation_number}
                  onChange={(e) => setFormData({ ...formData, rate_confirmation_number: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Booked By*
                </label>
                <input
                  type="text"
                  value={formData.booked_by}
                  onChange={(e) => setFormData({ ...formData, booked_by: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Carrier
                </label>
                <select
                  value={formData.carrier}
                  onChange={(e) => setFormData({ ...formData, carrier: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Carrier</option>
                  {carrierCompanies.length > 0 ? (
                    carrierCompanies.map((company) => (
                      <option key={company.id} value={company.name}>
                        {company.name}
                      </option>
                    ))
                  ) : (
                    // Companies not loaded (yet, or blocked): keep the saved value as an
                    // option so it is not silently blanked out when the form is saved.
                    formData.carrier && (
                      <option value={formData.carrier}>{formData.carrier}</option>
                    )
                  )}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Bill of Lading #
                </label>
                <input
                  type="text"
                  value={formData.bol_numbers}
                  onChange={(e) => setFormData({ ...formData, bol_numbers: e.target.value })}
                  placeholder="Multiple separated by comma"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Load Number*
                </label>
                <input
                  type="text"
                  value={formData.load_number || ''}
                  onChange={(e) => setFormData({ ...formData, load_number: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  style={{ color: 'black' }}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Commodity*
                </label>
                <input
                  type="text"
                  value={formData.commodity}
                  onChange={(e) => setFormData({ ...formData, commodity: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Weight (lbs)*
                </label>
                <input
                  type="number"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Total Rate ($)*
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.total_rate}
                  onChange={(e) => setFormData({ ...formData, total_rate: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Loaded Miles*
                </label>
                <input
                  type="number"
                  value={formData.total_miles}
                  onChange={(e) => setFormData({ ...formData, total_miles: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Assigned Driver — only shown when a dispatch exists */}
              {dispatchInfo && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Assigned Driver
                  </label>
                  {editingDriver ? (() => {
                    const q = driverSearch.trim().toLowerCase();
                    const filtered = driverOptions.filter((d) => {
                      if (!q) return true;
                      return (
                        d.name.toLowerCase().includes(q) ||
                        (d.truckNumber || '').toLowerCase().includes(q) ||
                        (d.teamLabel || '').toLowerCase().includes(q)
                      );
                    });
                    const selected = driverOptions.find((d) => d.id === selectedDriverId) || null;
                    return (
                      <div className="border border-blue-500 rounded-lg bg-white overflow-hidden">
                        <input
                          type="text"
                          autoFocus
                          placeholder="Search by name, truck # or team..."
                          value={driverSearch}
                          onChange={(e) => setDriverSearch(e.target.value)}
                          disabled={savingDriver}
                          className="w-full px-4 py-2 border-b border-slate-200 text-sm focus:outline-none focus:ring-0"
                        />
                        <div className="max-h-56 overflow-y-auto p-1">
                          {!driverOptionsLoaded ? (
                            <p className="text-xs text-slate-400 italic text-center py-4">Loading drivers...</p>
                          ) : filtered.length === 0 ? (
                            <p className="text-xs text-slate-400 italic text-center py-4">No matching drivers</p>
                          ) : (
                            filtered.map((d) => (
                              <button
                                key={d.id}
                                type="button"
                                onClick={() => setSelectedDriverId(d.id)}
                                disabled={savingDriver}
                                className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-md text-left text-sm transition-colors ${
                                  selectedDriverId === d.id
                                    ? 'bg-blue-50 text-blue-900 border border-blue-400'
                                    : 'hover:bg-slate-100 border border-transparent'
                                }`}
                              >
                                <span className="font-medium truncate">
                                  {d.name}
                                  {d.id === dispatchInfo.driver_id && (
                                    <span className="ml-1 text-xs text-slate-400">(current)</span>
                                  )}
                                  {d.teamLabel && (
                                    <span className="ml-1 text-xs text-purple-600">Team: {d.teamLabel}</span>
                                  )}
                                </span>
                                <span className="text-xs text-slate-500 whitespace-nowrap">
                                  {d.truckNumber ? `Truck ${d.truckNumber}` : 'No Truck'}
                                </span>
                              </button>
                            ))
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2 px-2 py-2 border-t border-slate-200 bg-slate-50">
                          <button
                            type="button"
                            onClick={() => setEditingDriver(false)}
                            disabled={savingDriver}
                            className="px-3 py-1.5 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm disabled:opacity-50"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleSaveDriver}
                            disabled={savingDriver || !selectedDriverId || selectedDriverId === dispatchInfo.driver_id}
                            className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed truncate"
                          >
                            {savingDriver
                              ? 'Saving...'
                              : selected && selected.id !== dispatchInfo.driver_id
                                ? `Assign ${selected.name}`
                                : 'Select a driver'}
                          </button>
                        </div>
                      </div>
                    );
                  })() : (
                    <div
                      onClick={handleStartEditDriver}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 cursor-pointer hover:bg-slate-100 transition-colors"
                      title="Click to change the assigned driver"
                    >
                      <span className="font-semibold">{dispatchInfo.driver_name}</span>
                      {dispatchInfo.truck_number && dispatchInfo.truck_number !== 'N/A' && (
                        <span className="text-slate-500 text-sm ml-2">· Truck {dispatchInfo.truck_number}</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Empty Miles + Loaded Miles — only shown when a dispatch exists,
                  since empty_miles is stored on dispatches. */}
              {dispatchInfo && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Empty Miles
                    </label>
                    {editingEmptyMiles ? (
                      <div className="flex gap-2">
                        <input
                          type="number"
                          value={emptyMilesValue}
                          onChange={(e) => setEmptyMilesValue(e.target.value)}
                          className="flex-1 px-4 py-2 border border-blue-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={handleSaveEmptyMiles}
                          className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingEmptyMiles(false);
                            setEmptyMilesValue((dispatchInfo.empty_miles ?? 0).toString());
                          }}
                          className="px-3 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => setEditingEmptyMiles(true)}
                        className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 cursor-pointer hover:bg-slate-100 transition-colors"
                        title="Click to edit empty miles"
                      >
                        <span className="font-semibold">{dispatchInfo.empty_miles ?? 0}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Total Miles
                    </label>
                    <div className="w-full px-4 py-2 bg-slate-100 border border-slate-300 rounded-lg text-slate-900">
                      <span className="font-semibold">
                        {(parseInt(formData.total_miles) || 0) + (dispatchInfo.empty_miles ?? 0)}
                      </span>
                      <span className="text-xs text-slate-500 ml-2">(Loaded + Empty)</span>
                    </div>
                  </div>
                </>
              )}

              {/* Driver Pay field - only shown if dispatch exists */}
              {dispatchInfo && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Driver Pay ($)
                  </label>
                  {editingDriverPay ? (
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="0.01"
                        value={driverPayValue}
                        onChange={(e) => setDriverPayValue(e.target.value)}
                        className="flex-1 px-4 py-2 border border-blue-500 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        autoFocus
                      />
                      <button
                        type="button"
                        onClick={handleSaveDriverPay}
                        className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingDriverPay(false);
                          setDriverPayValue(dispatchInfo.driver_pay?.toString() || '');
                        }}
                        className="px-3 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div
                      onClick={() => setEditingDriverPay(true)}
                      className="w-full px-4 py-2 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 cursor-pointer hover:bg-slate-100 transition-colors"
                      title="Click to edit driver pay"
                    >
                      {dispatchInfo.driver_pay ? (
                        <span className="font-semibold text-green-700">
                          ${Number(dispatchInfo.driver_pay).toLocaleString('en-US', { 
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2 
                          })}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">Click to add...</span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Document Upload Section */}
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h3 className="text-md font-semibold text-slate-900 mb-4">Upload Documents</h3>
              
              <div className="space-y-4">
                <div>
                  <input
                    type="file"
                    id="document-upload-edit"
                    multiple
                    onChange={handleDocumentUpload}
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  <label
                    htmlFor="document-upload-edit"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer font-medium"
                  >
                    <Upload className="w-4 h-4" />
                    Select Documents
                  </label>
                  <p className="text-xs text-slate-500 mt-2">
                    Upload rate confirmations, BOL, or other documents (PDF, DOC, Images)
                  </p>
                </div>

                {/* Display selected documents */}
                {uploadedDocuments.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">
                      Selected Documents ({uploadedDocuments.length}):
                    </p>
                    <div className="space-y-2">
                      {uploadedDocuments.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Upload className="w-4 h-4 text-blue-600 flex-shrink-0" />
                            <span className="text-sm text-slate-700 truncate">{file.name}</span>
                            <span className="text-xs text-slate-500 flex-shrink-0">
                              ({(file.size / 1024).toFixed(2)} KB)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeDocument(index)}
                            className="ml-2 p-1 text-red-600 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Route Section */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-6">Route</h2>
            
            {/* Shipper Details */}
            <div className="mb-8">
              <h3 className="text-md font-semibold text-slate-900 mb-4">Shipper Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Facility Name</label>
                  <input
                    type="text"
                    value={shipper.facility_name}
                    onChange={(e) => setShipper({ ...shipper, facility_name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Street</label>
                  <input
                    type="text"
                    value={shipper.street}
                    onChange={(e) => setShipper({ ...shipper, street: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">City*</label>
                  <input
                    type="text"
                    value={shipper.city}
                    onChange={(e) => setShipper({ ...shipper, city: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">State*</label>
                  <input
                    type="text"
                    value={shipper.state}
                    onChange={(e) => setShipper({ ...shipper, state: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Zip Code*</label>
                  <input
                    type="text"
                    value={shipper.zip_code}
                    onChange={(e) => setShipper({ ...shipper, zip_code: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date*</label>
                  <input
                    type="date"
                    value={shipper.date ? shipper.date.split('T')[0] : ''}
                    onChange={(e) => setShipper({ ...shipper, date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Time From*</label>
                  <input
                    type="time"
                    value={shipper.time_from}
                    onChange={(e) => setShipper({ ...shipper, time_from: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Time To</label>
                  <input
                    type="time"
                    value={shipper.time_to}
                    onChange={(e) => setShipper({ ...shipper, time_to: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contact Person</label>
                  <input
                    type="text"
                    value={shipper.contact_person}
                    onChange={(e) => setShipper({ ...shipper, contact_person: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      value={shipper.phone_number}
                      onChange={(e) => setShipper({ ...shipper, phone_number: e.target.value })}
                      className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <input
                      type="text"
                      value={shipper.phone_extension}
                      onChange={(e) => setShipper({ ...shipper, phone_extension: e.target.value })}
                      placeholder="ext"
                      className="w-20 px-2 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="lg:col-span-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Instructions</label>
                  <textarea
                    value={shipper.instructions}
                    onChange={(e) => setShipper({ ...shipper, instructions: e.target.value })}
                    maxLength={2000}
                    rows={3}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">Max length: 2000 characters</p>
                </div>
              </div>
            </div>

            {/* Consignee Details */}
            <div>
              <h3 className="text-md font-semibold text-slate-900 mb-4">Consignee Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Facility Name</label>
                  <input
                    type="text"
                    value={consignee.facility_name}
                    onChange={(e) => setConsignee({ ...consignee, facility_name: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Street</label>
                  <input
                    type="text"
                    value={consignee.street}
                    onChange={(e) => setConsignee({ ...consignee, street: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">City*</label>
                  <input
                    type="text"
                    value={consignee.city}
                    onChange={(e) => setConsignee({ ...consignee, city: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">State*</label>
                  <input
                    type="text"
                    value={consignee.state}
                    onChange={(e) => setConsignee({ ...consignee, state: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Zip Code*</label>
                  <input
                    type="text"
                    value={consignee.zip_code}
                    onChange={(e) => setConsignee({ ...consignee, zip_code: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date*</label>
                  <input
                    type="date"
                    value={consignee.date ? consignee.date.split('T')[0] : ''}
                    onChange={(e) => setConsignee({ ...consignee, date: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Time From*</label>
                  <input
                    type="time"
                    value={consignee.time_from}
                    onChange={(e) => setConsignee({ ...consignee, time_from: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Time To</label>
                  <input
                    type="time"
                    value={consignee.time_to}
                    onChange={(e) => setConsignee({ ...consignee, time_to: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Rescheduled Delivery Date/Time (Optional) */}
                <div className="lg:col-span-3">
                  <div className="border-t border-slate-200 pt-4 mt-2">
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Rescheduled Delivery (Optional)</h4>
                    <p className="text-xs text-slate-500 mb-3">Use these fields if the delivery was moved or rescheduled due to problems</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">New Date</label>
                        <input
                          type="date"
                          value={consignee.rescheduled_date ? consignee.rescheduled_date.split('T')[0] : ''}
                          onChange={(e) => setConsignee({ ...consignee, rescheduled_date: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">New Time From</label>
                        <input
                          type="time"
                          value={consignee.rescheduled_time_from}
                          onChange={(e) => setConsignee({ ...consignee, rescheduled_time_from: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">New Time To</label>
                        <input
                          type="time"
                          value={consignee.rescheduled_time_to}
                          onChange={(e) => setConsignee({ ...consignee, rescheduled_time_to: e.target.value })}
                          className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Contact Person</label>
                  <input
                    type="text"
                    value={consignee.contact_person}
                    onChange={(e) => setConsignee({ ...consignee, contact_person: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      value={consignee.phone_number}
                      onChange={(e) => setConsignee({ ...consignee, phone_number: e.target.value })}
                      className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <input
                      type="text"
                      value={consignee.phone_extension}
                      onChange={(e) => setConsignee({ ...consignee, phone_extension: e.target.value })}
                      placeholder="ext"
                      className="w-20 px-2 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div className="lg:col-span-3">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Instructions</label>
                  <textarea
                    value={consignee.instructions}
                    onChange={(e) => setConsignee({ ...consignee, instructions: e.target.value })}
                    maxLength={2000}
                    rows={3}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-xs text-slate-500 mt-1">Max length: 2000 characters</p>
                </div>
              </div>
            </div>
          </div>

          {/* Freight Section - Collapsible */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setFreightExpanded(!freightExpanded)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <h2 className="text-lg font-semibold text-slate-900">Freight</h2>
              {freightExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>

            {freightExpanded && (
              <div className="p-6 border-t border-slate-200">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left text-sm font-semibold text-slate-700 pb-3">Description</th>
                        <th className="text-left text-sm font-semibold text-slate-700 pb-3">Quantity</th>
                        <th className="text-left text-sm font-semibold text-slate-700 pb-3">Weight</th>
                        <th className="text-left text-sm font-semibold text-slate-700 pb-3">Declared Value</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {freightItems.map((item) => (
                        <tr key={item.id} className="border-b border-slate-100">
                          <td className="py-3">
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => updateFreightItem(item.id, 'description', e.target.value)}
                              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </td>
                          <td className="py-3">
                            <div className="flex gap-2">
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateFreightItem(item.id, 'quantity', e.target.value)}
                                className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <select
                                value={item.quantity_unit}
                                onChange={(e) => updateFreightItem(item.id, 'quantity_unit', e.target.value)}
                                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option>Pallet(s)</option>
                                <option>Piece(s)</option>
                                <option>Crate(s)</option>
                                <option>Box(es)</option>
                                <option>Bundle(s)</option>
                                <option>Unit(s)</option>
                              </select>
                            </div>
                          </td>
                          <td className="py-3">
                            <div className="flex gap-2">
                              <input
                                type="number"
                                value={item.weight}
                                onChange={(e) => updateFreightItem(item.id, 'weight', e.target.value)}
                                className="w-24 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <select
                                value={item.weight_unit}
                                onChange={(e) => updateFreightItem(item.id, 'weight_unit', e.target.value)}
                                className="px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option>lbs</option>
                                <option>tons</option>
                              </select>
                            </div>
                          </td>
                          <td className="py-3">
                            <input
                              type="number"
                              step="0.01"
                              value={item.declared_value}
                              onChange={(e) => updateFreightItem(item.id, 'declared_value', e.target.value)}
                              placeholder="$0.00"
                              className="w-32 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </td>
                          <td className="py-3">
                            <button
                              type="button"
                              onClick={() => removeFreightItem(item.id)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button
                  type="button"
                  onClick={addFreightItem}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add New Line
                </button>
              </div>
            )}
          </div>

          {/* Billing Section - Collapsible */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setBillingExpanded(!billingExpanded)}
              className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
            >
              <h2 className="text-lg font-semibold text-slate-900">Billing</h2>
              {billingExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>

            {billingExpanded && (
              <div className="p-6 border-t border-slate-200 space-y-6">
                {/* Customer Information */}
                <div>
                  <h3 className="text-md font-semibold text-slate-900 mb-4">Customer Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="lg:col-span-3">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Customer Name
                      </label>
                      <input
                        type="text"
                        value={billing.customer_name}
                        onChange={(e) => setBilling({ ...billing, customer_name: e.target.value })}
                        placeholder="Customer name..."
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Contact Info</label>
                      <input
                        type="text"
                        value={billing.contact_info}
                        onChange={(e) => setBilling({ ...billing, contact_info: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Street</label>
                      <input
                        type="text"
                        value={billing.street}
                        onChange={(e) => setBilling({ ...billing, street: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
                      <input
                        type="text"
                        value={billing.city}
                        onChange={(e) => setBilling({ ...billing, city: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
                      <input
                        type="text"
                        value={billing.state}
                        onChange={(e) => setBilling({ ...billing, state: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Zip Code</label>
                      <input
                        type="text"
                        value={billing.zip_code}
                        onChange={(e) => setBilling({ ...billing, zip_code: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={billing.email}
                        onChange={(e) => setBilling({ ...billing, email: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                      <div className="flex gap-2">
                        <input
                          type="tel"
                          value={billing.phone_number}
                          onChange={(e) => setBilling({ ...billing, phone_number: e.target.value })}
                          className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <input
                          type="text"
                          value={billing.phone_extension}
                          onChange={(e) => setBilling({ ...billing, phone_extension: e.target.value })}
                          placeholder="ext"
                          className="w-20 px-2 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Factoring Company</label>
                      <input
                        type="text"
                        value={billing.factoring_company}
                        onChange={(e) => setBilling({ ...billing, factoring_company: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Load Factoring Fee (%)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={billing.factoring_fee_percentage}
                        onChange={(e) => setBilling({ ...billing, factoring_fee_percentage: e.target.value })}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Transfer Options */}
                <div className="space-y-3">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={billing.transfer_documents}
                      onChange={(e) => setBilling({ ...billing, transfer_documents: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-700">Transfer Documents</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={billing.send_to_customer}
                      onChange={(e) => setBilling({ ...billing, send_to_customer: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-700">Send to Customer</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={billing.transfer_to_factoring}
                      onChange={(e) => setBilling({ ...billing, transfer_to_factoring: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-700">Transfer to Factoring</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
